import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Container, Grid, Link, List, ListItem, ListItemIcon, ListItemText, Paper, Stack, Typography, useTheme,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PublicHeader from '../components/Public/PublicHeader';
import PublicFooter from '../components/Public/PublicFooter';
import Screenshot from '../components/Public/Screenshot';

// ---------------------------------------------------------------------------
// Tour content — one section per stage of an incident
// ---------------------------------------------------------------------------

const sections = [
  {
    id: 'ingest',
    nav: 'Ingest',
    title: 'Ingest from anywhere',
    lead: 'Connect your EDRs natively and send everything else as it is. Excubia takes alerts in whatever shape they arrive and turns them into structured detections.',
    bullets: [
      'Five native EDR connectors — CrowdStrike, SentinelOne, Microsoft Defender, Palo Alto Cortex XDR and Sophos — with endpoint inventory, one-click isolation and alert status sync',
      'SIEM, email security, cloud audit logs, firewalls and IDS/IPS through the alert intake API',
      'Raw JSON and plain log lines are parsed by AI — no schema mapping required',
      'Threat-intel feeds in text, CSV, STIX or TAXII, pulled on a schedule and matched against every alert',
    ],
    primary: {
      src: '/screens/integrations.webp',
      alt: 'Integrations page showing five connected EDR agents — Sophos, Palo Alto Cortex XDR, Microsoft Defender, SentinelOne and CrowdStrike — with capability chips for each',
      caption: 'Five EDR agents connected, each showing its capabilities: alert ingestion, endpoint inventory, isolation and status sync.',
    },
    secondary: [
      {
        src: '/screens/integrations-endpoints.webp',
        alt: 'Endpoint inventory table listing hosts from every connected EDR vendor with operating system, status and last-seen time',
        caption: 'One endpoint inventory across every connected vendor.',
      },
    ],
  },
  {
    id: 'triage',
    nav: 'AI triage',
    title: 'AI triage on arrival',
    lead: 'Every alert is read, classified and scored the moment it lands, so analysts start with a verdict and the evidence behind it — not a raw payload.',
    bullets: [
      'Classification and category assigned automatically',
      'Severity scored with the reasoning shown',
      'Indicators extracted: IPs, domains, URLs and file hashes',
      'Hosts, users and processes pulled out and linked as entities',
      'Confidence is shown on every analysis — 95% for this Log4Shell exploit',
    ],
    primary: {
      src: '/screens/detection-detail.webp',
      alt: 'Critical Log4Shell detection with severity and status chips, quick actions including Isolate endpoint via CrowdStrike, and a description listing host, user and command line',
      caption: 'A critical Log4Shell detection, with quick actions including endpoint isolation through CrowdStrike.',
    },
    secondary: [
      {
        src: '/screens/ai-analysis.webp',
        alt: 'AI Analysis card summarising the Log4Shell detection with 95% confidence',
        caption: 'AI analysis: what happened, why it matters, 95% confidence.',
      },
      {
        src: '/screens/vendor-alert.webp',
        alt: 'CrowdStrike alert card with host, process, command line, SHA256 hash and MITRE ATT&CK technique chips',
        caption: 'The original vendor alert, with host, process, command line, SHA256 and ATT&CK techniques.',
      },
    ],
  },
  {
    id: 'threat-intel',
    nav: 'Threat intel',
    title: 'Threat intelligence, automatically',
    lead: 'Indicators are checked against every configured provider and condensed into one verdict with the reasoning behind it.',
    bullets: [
      'Eight providers: VirusTotal, AbuseIPDB, AlienVault OTX, abuse.ch, GreyNoise, Shodan, GeoIP and DNS',
      'A verdict with reasoning, not just a pile of scores',
      'Disagreements between providers are surfaced, not hidden',
      'Results cached for 24 hours to keep lookups fast and within quota',
      'Severity escalates automatically when an indicator comes back malicious',
    ],
    primary: {
      src: '/screens/threat-intel.webp',
      frame: 'card',
      maxHeight: 560,
      alt: 'Threat Intelligence card showing a Malicious verdict and an AI summary of results from several providers',
      caption: 'One verdict across providers, with an AI summary explaining why.',
    },
    secondary: [
      {
        src: '/screens/integrations-threat-intel.webp',
        alt: 'Threat-intelligence provider cards for VirusTotal, AbuseIPDB, AlienVault OTX, GreyNoise and others, each with its connection status',
        caption: 'Turn providers on with an API key; enrichment starts immediately.',
      },
    ],
  },
  {
    id: 'correlation',
    nav: 'Correlation',
    title: 'AI correlation into cases',
    lead: 'Related detections are grouped into a single case, so a multi-stage intrusion is one piece of work — not twenty duplicates.',
    bullets: [
      'Detections related by host, user, indicator and time are grouped together',
      'The AI’s reasoning for the grouping is stored on the case',
      'Detections are closed as they are assigned to a case, so the queue stays clean',
      'Every case carries an SLA countdown from the moment it opens',
    ],
    primary: {
      src: '/screens/cases.webp',
      alt: 'Cases list with severity, status and assignee columns and an SLA countdown chip for each case',
      caption: 'The cases list, with an SLA countdown on every case.',
    },
    secondary: [
      {
        src: '/screens/case-detail.webp',
        alt: 'Case header with severity and priority chips, overdue SLA chips, AI reasoning in the description and an SLA card',
        caption: 'A correlated case: AI reasoning in the description, SLA status alongside.',
      },
    ],
  },
  {
    id: 'timeline',
    nav: 'Timeline & report',
    title: 'Attack timeline & incident report',
    lead: 'Excubia rebuilds what the attacker did, step by step, and writes the report you would otherwise spend an afternoon on.',
    bullets: [
      'A verdict up front — here, a confirmed intrusion at 98% confidence',
      'Every event with its process, command line, file hashes and network connections',
      'A chronological technical narrative plus an executive summary',
      'Recommended next steps for containment and recovery',
      'Copy the report as Markdown into your ticket, wiki or handover',
    ],
    primary: {
      src: '/screens/attack-timeline.webp',
      frame: 'card',
      maxHeight: 600,
      alt: 'Attack timeline with a "Confirmed intrusion, confidence 98%" narrative followed by each event with process, command line, hashes and network activity',
      caption: 'The attack timeline: a confirmed-intrusion verdict, then every step in order.',
    },
    secondary: [
      {
        src: '/screens/ai-report.webp',
        alt: 'AI incident report with a chronological technical narrative of the intrusion',
        caption: 'The AI incident report, ready to copy as Markdown.',
      },
    ],
  },
  {
    id: 'respond',
    nav: 'Respond',
    title: 'Respond with playbooks, SLAs and automation',
    lead: 'Codify how your SOC responds so it happens the same way on every shift — and see immediately when something is running late.',
    bullets: [
      'Per-severity SLA targets for response and resolution, with breach monitoring',
      'Playbooks with task checklists for consistent response steps',
      'Automation rules: set severity, assign, tag, create a case or notify when conditions match',
      'Isolate an endpoint straight from the detection or case, through the vendor’s own API',
    ],
    primary: {
      src: '/screens/automation.webp',
      alt: 'Automation rules settings page explaining how conditions trigger actions such as setting severity, assigning, tagging, creating a case or notifying',
      caption: 'Automation rules: conditions trigger actions on every new detection.',
    },
    secondary: [
      {
        src: '/screens/sla.webp',
        fit: 'contain',
        alt: 'SLA card showing response and resolution targets and time remaining for a case',
        caption: 'Response and resolution targets on every case.',
      },
      {
        src: '/screens/needs-attention.webp',
        alt: 'Needs attention list of cases that have breached or are about to breach their SLA',
        caption: 'Breached and at-risk cases surface on the dashboard.',
      },
    ],
  },
  {
    id: 'measure',
    nav: 'Measure',
    title: 'Measure and report',
    lead: 'Show how the SOC is performing and where attackers are active, without building a spreadsheet.',
    bullets: [
      'Mean time to acknowledge and mean time to resolve (MTTA / MTTR)',
      'SLA compliance over time',
      'MITRE ATT&CK coverage heatmap',
      'Top hosts, users and techniques',
      'CSV export of detections, cases and reports',
      'A daily email digest so nothing slips overnight',
    ],
    primary: {
      src: '/screens/reports.webp',
      alt: 'Reports page with KPI tiles and charts of detections by severity, cases created versus resolved, and breakdowns by source, status, category and vendor',
      caption: 'Reports: KPIs, trends and breakdowns by source, status, category and vendor.',
    },
    secondary: [
      {
        src: '/screens/mitre-heatmap.webp',
        position: 'left top',
        alt: 'MITRE ATT&CK coverage heatmap with detection counts by tactic and technique',
        caption: 'MITRE ATT&CK coverage.',
      },
      {
        src: '/screens/daily-severity.webp',
        fit: 'contain',
        alt: 'Bar chart of daily detections stacked by severity',
        caption: 'Daily detections by severity.',
      },
      {
        src: '/screens/top-techniques.webp',
        alt: 'Table of the most frequently observed MITRE ATT&CK techniques with counts',
        caption: 'Top MITRE techniques.',
      },
    ],
  },
  {
    id: 'more',
    nav: 'And more',
    title: 'Entities, indicators and everything else',
    lead: 'The rest of what a SOC needs day to day — built in, not bolted on.',
    bullets: [
      'Entity risk scores that decay over time, so they reflect recent activity',
      'Users linked to the devices they use, across every detection',
      'Push indicators to your EDRs to block them everywhere at once',
      'Dark mode and global search across detections, cases, entities and indicators',
      'Jira and ServiceNow ticketing; webhooks, Splunk and Elastic outbound',
      'Self-service backups and restore',
      'Tenant-scoped data, custom roles and a full audit log',
    ],
    primary: {
      src: '/screens/entities.webp',
      alt: 'Entities list of hosts and users with risk scores',
      caption: 'Entities with risk scores that decay over time.',
    },
    secondary: [
      {
        src: '/screens/entity-detail.webp',
        alt: 'Entity detail page showing a host’s risk score, linked users and related detections',
        caption: 'An entity with its linked users, devices and detections.',
      },
      {
        src: '/screens/indicators.webp',
        alt: 'Indicator table with confidence, TLP, source and sightings for each indicator of compromise',
        caption: 'Indicators with confidence, TLP, source and sightings.',
      },
      {
        src: '/screens/dashboard-dark.webp',
        alt: 'The SOC dashboard in dark mode',
        caption: 'Dark mode, for the night shift.',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const HEADER_HEIGHT = { xs: 56, sm: 64 };

const scrollToSection = (id) => (event) => {
  const el = document.getElementById(id);
  if (!el) return;
  event.preventDefault();
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.history.replaceState(null, '', `#${id}`);
};

const Bullets = ({ items }) => (
  <List dense disablePadding>
    {items.map((b) => (
      <ListItem key={b} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
        <ListItemIcon sx={{ minWidth: 30, mt: 0.4 }}>
          <CheckCircleIcon color="primary" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={b} primaryTypographyProps={{ variant: 'body1' }} />
      </ListItem>
    ))}
  </List>
);

const TourSection = ({ section, index, bg }) => {
  const reverse = index % 2 === 1;
  const secondaryCols = section.secondary.length >= 3 ? 4 : 6;
  return (
    <Box
      component="section"
      id={section.id}
      aria-labelledby={`${section.id}-heading`}
      sx={{ bgcolor: bg, py: { xs: 7, md: 10 }, scrollMarginTop: { xs: 56 + 52, sm: 64 + 52 } }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" direction={{ xs: 'row', md: reverse ? 'row-reverse' : 'row' }}>
          <Grid item xs={12} md={5}>
            <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 700, letterSpacing: 1.5 }}>
              Step {index + 1}
            </Typography>
            <Typography id={`${section.id}-heading`} variant="h4" component="h2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              {section.title}
            </Typography>
            <Typography variant="h6" component="p" sx={{ color: 'text.secondary', fontWeight: 400, mb: 2 }}>
              {section.lead}
            </Typography>
            <Bullets items={section.bullets} />
          </Grid>
          <Grid item xs={12} md={7}>
            <Screenshot frame="browser" {...section.primary} eager={index === 0} />
          </Grid>
        </Grid>
        <Grid container spacing={4} justifyContent="center" sx={{ mt: { xs: 1, md: 3 } }}>
          {section.secondary.map((shot) => (
            <Grid item xs={12} sm={6} md={secondaryCols} key={shot.src}>
              <Screenshot frame="card" aspectRatio="16 / 10" showCaption {...shot} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const ProductPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [active, setActive] = useState(sections[0].id);
  const altBg = theme.palette.mode === 'dark' ? grey[900] : grey[50];

  useEffect(() => {
    const previous = document.title;
    document.title = 'Product tour · Excubia';
    // Deep links such as /product#timeline: jump once the sections have rendered.
    const hash = window.location.hash.slice(1);
    if (hash && sections.some((s) => s.id === hash)) {
      document.getElementById(hash)?.scrollIntoView({ block: 'start' });
    }
    return () => { document.title = previous; };
  }, []);

  // Highlight the section currently in view in the sticky nav.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: '-35% 0px -55% 0px' },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Keep the active nav link visible in the horizontally scrolling bar.
  useEffect(() => {
    const link = document.getElementById(`tour-nav-${active}`);
    const bar = link?.parentElement;
    if (link && bar && bar.scrollWidth > bar.clientWidth) {
      bar.scrollTo({ left: link.offsetLeft - bar.clientWidth / 2 + link.clientWidth / 2, behavior: 'smooth' });
    }
  }, [active]);

  const heroBg = theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #0b1220 0%, #1a2540 100%)'
    : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflowX: 'clip' }}>
      <PublicHeader />

      {/* Hero */}
      <Box sx={{ background: heroBg, color: 'common.white', py: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                icon={<PlayCircleOutlineIcon />}
                label="Follow one incident from alert to report"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'common.white', mb: 2, '& .MuiChip-icon': { color: 'common.white' } }}
              />
              <Typography variant="h2" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2.25rem', md: '3.25rem' }, mb: 2 }}>
                Product tour
              </Typography>
              <Typography variant="h6" component="p" sx={{ fontWeight: 400, opacity: 0.92, mb: 3 }}>
                A Log4Shell exploit lands on a web server. Follow it through Excubia: ingested from the EDR, triaged by AI,
                enriched with threat intel, correlated into a case, reconstructed as an attack timeline, and handled
                against an SLA — with reporting at the end.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button variant="contained" color="secondary" size="large" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/register')}>
                  Start 60-day evaluation
                </Button>
                <Button
                  variant="outlined" size="large" href={`#${sections[0].id}`} onClick={scrollToSection(sections[0].id)}
                  sx={{ color: 'common.white', borderColor: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: 'common.white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  Start the tour
                </Button>
              </Stack>
              <Typography variant="body2" sx={{ mt: 3, opacity: 0.75, fontStyle: 'italic' }}>
                Screenshots from a live Excubia tenant fed by simulated EDR telemetry for a fictional organisation, Vantage Dynamics.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Box sx={{ filter: 'drop-shadow(0 30px 50px rgba(0,0,0,0.45))' }}>
                <Screenshot
                  src="/screens/dashboard.webp"
                  alt="Excubia SOC dashboard with KPI tiles for open detections, open cases, SLA breaches, automation and integrations, plus a needs-attention list and recent critical detections"
                  caption="The SOC dashboard for Vantage Dynamics, a fictional organisation fed by simulated EDR telemetry."
                  eager
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sticky in-page nav */}
      <Box
        component="nav"
        aria-label="Product tour sections"
        sx={{
          position: 'sticky', top: HEADER_HEIGHT, zIndex: theme.zIndex.appBar - 1,
          bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              py: 1, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
              justifyContent: { lg: 'center' },
            }}
          >
            {sections.map((s, i) => {
              const selected = active === s.id;
              return (
                <Link
                  key={s.id}
                  id={`tour-nav-${s.id}`}
                  href={`#${s.id}`}
                  onClick={scrollToSection(s.id)}
                  underline="none"
                  aria-current={selected ? 'location' : undefined}
                  sx={{
                    flexShrink: 0, px: 1.5, py: 0.75, borderRadius: 5, fontSize: '0.875rem', whiteSpace: 'nowrap',
                    fontWeight: selected ? 700 : 500,
                    color: selected ? 'primary.contrastText' : 'text.secondary',
                    bgcolor: selected ? 'primary.main' : 'transparent',
                    '&:hover': { bgcolor: selected ? 'primary.dark' : 'action.hover', color: selected ? 'primary.contrastText' : 'text.primary' },
                  }}
                >
                  {i + 1}. {s.nav}
                </Link>
              );
            })}
          </Stack>
        </Container>
      </Box>

      {sections.map((section, index) => (
        <TourSection key={section.id} section={section} index={index} bg={index % 2 === 0 ? 'background.default' : altBg} />
      ))}

      {/* CTA band */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Paper
          sx={{
            p: { xs: 4, md: 6 }, textAlign: 'center', color: 'common.white',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          }}
        >
          <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
            Try it on your own alerts
          </Typography>
          <Typography variant="h6" component="p" sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}>
            60-day evaluation. No card required. Connect an EDR or post your first alert in minutes.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/register')}
              sx={{ bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' }, px: 4 }}
            >
              Get started
            </Button>
            <Button
              variant="outlined" size="large" startIcon={<HandshakeIcon />} onClick={() => navigate('/partner')}
              sx={{ color: 'common.white', borderColor: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: 'common.white', bgcolor: 'rgba(255,255,255,0.1)' }, px: 4 }}
            >
              Partner with us
            </Button>
          </Stack>
        </Paper>
      </Container>

      <PublicFooter />
    </Box>
  );
};

export default ProductPage;
